import { useEffect, useState } from "react";
import {
  getCurrentQuestion,
  getCurrentQuiz,
  getLastQuizResults,
  quizGiveUp,
  startQuiz,
  useApi,
} from "../api/Api";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Fade,
  Grid,
  Typography,
} from "@mui/material";
import QuizStats from "../components/QuizStats";
import LoginForm from "../components/LoginForm";
import QuestionMultChoice from "../components/QuestionMultChoice";
import QuestionTrueFalse from "../components/QuestionTrueFalse";
import QuestionWriteAnswer from "../components/QuestionWriteAnswer";
import QuizResults from "../components/QuizResults";
import SubcategoryPreferences from "../components/SubcategoryPreferences";
import QuestionSequence from "../components/QuestionSequence";
import QuestionFillGaps from "../components/QuestionFillGaps";
import QuestionDragDrop from "../components/QuestionDragDrop";
import { useTranslation } from "react-i18next";
import React from "react";

const QuizPage = ({ langChanged }) => {
  const { t } = useTranslation();
  const api = useApi();

  const [stats, setStats] = useState();
  const [question, setQuestion] = useState();
  const [fetchError, setFetchError] = useState("");
  const [createError, setCreateError] = useState("");
  const [results, setResults] = useState();

  const [giveUpDialogOpen, setGiveUpDialogOpen] = useState(false);

  const openGiveUpDialog = () => {
    setGiveUpDialogOpen(true);
  };

  const closeGiveUpDialog = () => {
    setGiveUpDialogOpen(false);
  };

  useEffect(() => {
    fetchQuizStats();
    fetchCurrentQuestion();
  }, []);

  const fetchCurrentQuestion = async () => {
    try {
      const data = (await getCurrentQuestion(api)).data;
      setQuestion(data);
    } catch (error) {
      console.log(error.response);
    }
  };

  const fetchQuizStats = () => {
    getCurrentQuiz(api)
      .then((response) => setStats(response.data))
      .catch((error) => {
        console.log(error.response);
        setStats(undefined);
        setQuestion(undefined);
        getLastQuizResults(api)
          .then((response) => setResults(response.data))
          .catch((error) => setFetchError(error.response["data"]));
      });
  };

  const createQuiz = () => {
    startQuiz(api)
      .then(() => window.location.reload())
      .catch((error) => setCreateError(error.response["data"]));
  };

  const giveUp = () => {
    quizGiveUp(api)
      .then(() => {
        fetchQuizStats();
        fetchCurrentQuestion();
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    fetchCurrentQuestion();
    fetchQuizStats();
  }, [langChanged]);

  const renderQuestion = (question) => {
    const props = {
      question,
      fetchQuizStats,
      fetchCurrentQuestion,
      key: question.id,
    };

    if (!question) return <Alert severity="error">Question not found.</Alert>;

    switch (question["type"]) {
      case "MULT_CHOICE":
        return <QuestionMultChoice {...props} />;
      case "TRUE_FALSE":
        return <QuestionTrueFalse {...props} />;
      case "WRITE_ANSWER":
        return <QuestionWriteAnswer {...props} />;
      case "SEQUENCE":
        return <QuestionSequence {...props} />;
      case "FILL_GAPS":
        return <QuestionFillGaps {...props} />;
      case "DRAG_DROP":
        return <QuestionDragDrop {...props} />;
      default:
        return (
          <Alert severity="error">
            Invalid question type: {question["type"]}
          </Alert>
        );
    }
  };

  return (
    <>
      {window.localStorage.getItem("token") === null ? (
        <Grid container justifyContent="center">
          <Alert
            severity="warning"
            variant="filled"
            sx={{ mt: 2, maxWidth: 500 }}
          >
            {t("login_quiz_prompt")}
          </Alert>
          <LoginForm />
        </Grid>
      ) : (
        <>
          {stats === undefined || !!fetchError ? (
            <>
              {results === undefined ? (
                <Box sx={{ m: 2 }}>
                  <Grid container justifyContent="center">
                    <Alert severity="warning" variant="filled">
                      {fetchError}
                    </Alert>
                  </Grid>
                  <Grid container justifyContent="center" sx={{ mt: 2 }}>
                    <SubcategoryPreferences langChanged={langChanged} />
                  </Grid>
                  <Grid container justifyContent="center" sx={{ mt: 2 }}>
                    <Button variant="contained" onClick={createQuiz}>
                      {t("start_quiz")}
                    </Button>
                  </Grid>
                  <Grid container justifyContent="center" sx={{ mt: 2 }}>
                    {!!createError ? (
                      <Fade in={true}>
                        <Grid item xs={12}>
                          <Alert severity="error">{createError}</Alert>
                        </Grid>
                      </Fade>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ m: 2 }}>
                  <Grid container justifyContent="center">
                    <QuizResults results={results} />
                  </Grid>
                  <Grid container justifyContent="center" sx={{ mt: 2 }}>
                    <SubcategoryPreferences langChanged={langChanged} />
                  </Grid>
                  <Grid container justifyContent="center" sx={{ mt: 2 }}>
                    <Button variant="contained" onClick={createQuiz}>
                      {t("start_new_quiz")}
                    </Button>
                  </Grid>
                  <Grid container justifyContent="center" sx={{ mt: 2 }}>
                    {!!createError ? (
                      <Fade in={true}>
                        <Grid item xs={12}>
                          <Alert severity="error">{createError}</Alert>
                        </Grid>
                      </Fade>
                    ) : (
                      <></>
                    )}
                  </Grid>
                </Box>
              )}
            </>
          ) : (
            <>
              <QuizStats
                startDate={stats["date_start"]}
                questionIdx={stats["question_idx"]}
                numQuestions={stats["question_accessors"].length}
                score={stats["score"]}
                attempts={stats["first_try"] + 1}
              ></QuizStats>
              {question === undefined ? (
                <CircularProgress />
              ) : (
                renderQuestion(question)
              )}
              <Grid container justifyContent="center" sx={{ my: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={openGiveUpDialog}
                >
                  {t("give_up")}
                </Button>
              </Grid>
              <Dialog onClose={closeGiveUpDialog} open={giveUpDialogOpen}>
                <Grid container justifyContent="center">
                  <DialogTitle>{t("give_up_confirmation")}</DialogTitle>
                </Grid>
                <Box sx={{ px: 2 }}>
                  <Typography justifyContent="justify">
                    {t("give_up_desc")}
                  </Typography>
                </Box>
                <Grid
                  container
                  justifyContent="center"
                  spacing={1}
                  sx={{ my: 2 }}
                >
                  <Grid item>
                    <Button variant="contained" onClick={closeGiveUpDialog}>
                      {t("give_up_cancel")}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button variant="contained" color="error" onClick={giveUp}>
                      {t("give_up_ok")}
                    </Button>
                  </Grid>
                </Grid>
              </Dialog>
            </>
          )}
        </>
      )}
    </>
  );
};

export default QuizPage;
